
import { UseFormReturn } from "react-hook-form";
import { ProcurementFormValues } from "../schema";
import { useEffect, useState } from "react";
import { addDays } from "date-fns";
import { PaymentTypeField } from "./PaymentTypeField";
import { PaymentTermDaysField } from "./PaymentTermDaysField";
import { AdvancePaymentSection } from "./AdvancePaymentSection";
import { DamageReturnSection } from "./DamageReturnSection";
import { PaymentDueDateDisplay } from "./PaymentDueDateDisplay";
import { BankInformationSection } from "./BankInformationSection";

interface PaymentTermsFieldsProps {
  form: UseFormReturn<ProcurementFormValues>;
}

export const PaymentTermsFields = ({ form }: PaymentTermsFieldsProps) => {
  const paymentTermType = form.watch("payment_term_type") || "upfront";
  const paymentTermDays = form.watch("payment_term_days") || 0;
  const expectedDeliveryDate = form.watch("expected_delivery_date");
  const hasAdvancePayment = form.watch("has_advance_payment") || false;
  const advancePaymentAmount = form.watch("advance_payment_amount") || 0;
  const totalAmount = form.watch("total_amount") || 0;
  const supportsDamageReturn = form.watch("supports_damage_return") || false;
  const [paymentDueDate, setPaymentDueDate] = useState<Date | null>(null);
  
  // Calculate payment due date based on payment term type
  useEffect(() => {
    if (paymentTermType === "upfront") {
      // For upfront payments, set due date to today
      const today = new Date();
      setPaymentDueDate(today);
      form.setValue("payment_due_date", today);
    } else if (paymentTermType === "term" && expectedDeliveryDate && paymentTermDays > 0) {
      const dueDate = addDays(new Date(expectedDeliveryDate), paymentTermDays);
      setPaymentDueDate(dueDate);
      form.setValue("payment_due_date", dueDate);
    } else {
      setPaymentDueDate(null);
      form.setValue("payment_due_date", null);
    }
  }, [paymentTermType, expectedDeliveryDate, paymentTermDays, form]);

  // Calculate remaining payment amount when advance payment changes
  useEffect(() => {
    if (hasAdvancePayment && advancePaymentAmount > 0 && totalAmount > 0) {
      const remaining = totalAmount - advancePaymentAmount;
      form.setValue("remaining_payment_amount", Math.max(0, remaining));
    } else {
      form.setValue("remaining_payment_amount", totalAmount);
    }
  }, [hasAdvancePayment, advancePaymentAmount, totalAmount, form]);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Payment Terms</h3>
      
      <PaymentTypeField form={form} />

      {paymentTermType === "term" && (
        <PaymentTermDaysField form={form} />
      )}

      <BankInformationSection form={form} />

      <AdvancePaymentSection
        form={form}
        hasAdvancePayment={hasAdvancePayment}
        advancePaymentAmount={advancePaymentAmount}
        totalAmount={totalAmount}
      />

      <PaymentDueDateDisplay 
        paymentDueDate={paymentDueDate}
        paymentTermType={paymentTermType}
      />

      <DamageReturnSection 
        form={form}
        supportsDamageReturn={supportsDamageReturn}
      />
    </div>
  );
};
